<template>
    <div class="application-edit__wrapper" ref="wrapper" tabindex="0" @keydown.esc="onReset">
        <div class="app__card_edit application-edit">
            <div class="app__card-header">
                {{ applicationEditTitle }}
            </div>
            <div class="app__card-body_edit">
                <form-select-group
                    class="application-edit__warehouse"
                    v-if="isStoreOperation && !$features.useDefaultWarehouse"
                    :label="$tc('applicationsFields.warehouse')"
                    :options="warehousesSelectOptions"
                    v-model="warehouseId"
                >
                </form-select-group>
                <div class="version-edit__icons">{{ $tc("words.icons") }}</div>
                <div
                    class="version-edit__icon-container"
                    v-for="(icon, index) in applicationData.versions[0].icons"
                    :key="index"
                >
                    <icon-form-field
                        v-model="applicationData.versions[0].icons[index]"
                        :help="$tc('applicationFieldHelp.icons')"
                    ></icon-form-field>
                </div>

                <!-- proper application fields -->
                <application-form-field
                    v-for="field in dataModelFields"
                    :key="field.name"
                    :field="field"
                    v-model="applicationData[field.name]"
                    :errors="formErrors[field.name]"
                    @change="clearFieldErrors(field)"
                ></application-form-field>

                <!-- version fields raised to application level -->
                <application-form-field
                    v-for="field in appVersionSynchroFields"
                    :key="field.name"
                    :field="field"
                    v-model="applicationData.versions[0][field.name]"
                    :errors="formErrors[field.name]"
                    @change="clearFieldErrors(field)"
                >
                </application-form-field>

                <div>
                    <sah-button @press="appendNewVersion" class="app__card-new-version-button">{{
                        $tc("versions.addNew")
                    }}</sah-button>
                </div>
                <!-- proper version fields -->
                <div
                    class="app__card-version-edit"
                    v-for="(version, indVersion) in applicationData.versions"
                    :key="version.id"
                >
                    <div class="app__card-version-title">
                        {{ $t("versions.title", {versionNumber: version.versionNumber}) }}
                        <div
                            v-if="hasMultipleVersions"
                            @click.stop="deleteVersion(indVersion)"
                            class="version-edit__icon-control app__card-version__delete-icon"
                            v-html="$tc('icons.delete')"
                            :title="$tc('words.delete')"
                        ></div>
                    </div>
                    <application-form-field
                        v-for="field in properVersionFields"
                        :key="field.name"
                        :field="field"
                        v-model="applicationData.versions[indVersion][field.name]"
                        :errors="formErrors[field.name]"
                        @change="clearFieldErrors(field)"
                    ></application-form-field>
                </div>
            </div>
            <div class="app__card-footer">
                <sah-button class="app__button_alt" @click.native="onReset">{{ $tc("words.cancel") }}</sah-button>
                <sah-button v-if="isStoreOperation" :disabled="hasFormErrors" @click.native="onSubmit">{{
                    $tc("applications.saveApplicationWithVersion")
                }}</sah-button>
                <sah-button v-if="isUpdateOperation" :disabled="hasFormErrors" @click.native="onSubmit">{{
                    $tc("words.save")
                }}</sah-button>
            </div>
        </div>
    </div>
</template>

<script>
import {store, FormSelectGroup, SahButton} from "atom";
import ApplicationFormField from "./ApplicationFormField";
import FormValidationMixin from "../mixins/FormValidationMixin";
import IconFormField from "./IconFormField";
import {
    EDIT_OPERATION_STORE,
    EDIT_OPERATION_UPDATE,
    APPLICATIONS_VERSIONS_SHARED_FIELDS,
    APPLICATIONS_TYPE_VERSIONS_EXCLUDED_FIELDS,
    VERSION_NUMBER_PATTERN,
} from "../constants";
const {createNamespacedHelpers} = store;
const {mapActions, mapGetters, mapState} = createNamespacedHelpers("arcman");

export default {
    name: "application-edit",
    props: {
        application: {type: Object, required: true},
        operation: {type: String, required: true},
    },
    components: {
        ApplicationFormField,
        FormSelectGroup,
        IconFormField,
        SahButton,
    },
    mixins: [FormValidationMixin],
    computed: {
        ...mapGetters(["currentLocale"]),
        ...mapState(["arcologyConstants", "warehouses"]),
        lastVersion() {
            return this.applicationData.versions[0];
        },
        dataModelFields() {
            return this.arcologyConstants.APPLICATION_FIELDS.filter(
                item => !(this.$settings.dataModels.APPLICATIONS_EXCLUDED_FIELDS?.indexOf(item.name) >= 0)
            );
        },
        appVersionSynchroFields() {
            return this.arcologyConstants.VERSION_FIELDS.filter(item =>
                APPLICATIONS_VERSIONS_SHARED_FIELDS.has(item.name)
            );
        },
        properVersionFields() {
            return this.arcologyConstants.VERSION_FIELDS.filter(
                field =>
                    !APPLICATIONS_TYPE_VERSIONS_EXCLUDED_FIELDS[this.application.type].has(field.name) &&
                    !(this.$settings.dataModels.VERSIONS_EXCLUDED_FIELDS?.indexOf(field.name) >= 0) &&
                    !APPLICATIONS_VERSIONS_SHARED_FIELDS.has(field.name)
            );
        },
        applicationEditTitle() {
            return this.operation === "store"
                ? this.$tc("applications.createApplication")
                : `${this.$tc("words.edit")} ${this.application.name[this.currentLocale]}`;
        },
        applicationTypeSuffix() {
            return this.application.type.split(" ").join("-");
        },

        warehousesSelectOptions() {
            let warehousesOptions = this.warehouses.map(element => ({text: element.name, value: element.id}));
            warehousesOptions.unshift({text: "", value: null, disabled: true});
            return warehousesOptions;
        },
        isStoreOperation() {
            return this.operation === EDIT_OPERATION_STORE;
        },
        isUpdateOperation() {
            return this.operation === EDIT_OPERATION_UPDATE;
        },
        hasMultipleVersions() {
            return this.application?.versions.length > 1;
        },
    },
    methods: {
        ...mapActions({
            updateApplication: "SAVE_APPLICATION",
            storeApplication: "CREATE_APPLICATION",
            destroyVersion: "DELETE_VERSION",
        }),
        saveApplication() {
            this.updateApplication(this.applicationData);
            this.$emit("end");
        },
        storeApplicationAndQueueStoreVersion() {
            this.storeApplication({application: this.applicationData, warehouseId: this.warehouseId});
            this.$emit("queueStoreVersion");
        },
        async deleteVersion(indVersion) {
            const confirm = await this.$modal.dialog(
                this.$t("versions.confirmDeleteVersionOfApplication", {
                    application: this.applicationData.name[this.currentLocale],
                    versionNumber: this.applicationData.versions[indVersion].versionNumber,
                })
            );
            if (confirm) {
                this.destroyVersion({
                    application: this.applicationData,
                    version: this.applicationData.versions[indVersion],
                });
                this.applicationData.versions.splice(indVersion, 1);
            }
            this.$modal.hide();
        },
        appendNewVersion() {
            const version = this.getCleanNewVersion();
            version.applicationId = this.application.id;
            // copy the common fields from the previous version
            this.appVersionSynchroFields.forEach(field => {
                version[field.name] = this.lastVersion[field.name];
            });
            // ...and the icon
            version.icons = this.lastVersion.icons;
            this.applicationData.versions.unshift(version);
        },
        onSubmit() {
            // form validation
            let isValid = this.validateForm(this.dataModelFields, this.applicationData);
            this.applicationData.versions.forEach(version => {
                isValid =
                    isValid &&
                    this.validateForm(this.appVersionSynchroFields, version) &&
                    this.validateForm(this.properVersionFields, version);
            });
            if (isValid) {
                if (this.isStoreOperation) {
                    this.storeApplicationAndQueueStoreVersion();
                } else if (this.isUpdateOperation) {
                    this.saveApplication();
                }
                this.$emit("end");
            }
        },
        onReset() {
            this.$emit("cancel");
        },
        getCleanNewVersion() {
            const version = {};
            const versionTemplate = JSON.parse(JSON.stringify(this.arcologyConstants.VERSION_TEMPLATE));
            for (const [key, value] of Object.entries(versionTemplate)) {
                if (!APPLICATIONS_TYPE_VERSIONS_EXCLUDED_FIELDS[this.application.type].has(key)) {
                    version[key] = value;
                }
            }
            const icon = JSON.parse(JSON.stringify(this.arcologyConstants.PICTURE_TEMPLATE));
            version.icons.push(icon);
            version.date = new Date().toISOString().split("T")[0];
            version.versionNumber = VERSION_NUMBER_PATTERN;
            return Object.assign(version, JSON.parse(JSON.stringify(this.$settings.dataModels.DEFAULT_VERSION)));
        },
    },
    data() {
        return {
            warehouseId: "",
            applicationData: JSON.parse(JSON.stringify(this.application)),
        };
    },
    mounted() {
        this.$refs.wrapper.focus();
    },
};
</script>

<style lang="scss" scoped>
.application-edit__wrapper::v-deep {
    @apply fixed top-0 left-0 z-10 h-full w-full flex flex-row items-center justify-center;
    background-color: $background-wrapper-edit-color;

    .application-edit__warehouse {
        @apply mb-2;
    }

    .version-edit__icons {
        @apply font-bold mb-1;
    }

    .version-edit__new-icon-container {
        @apply flex justify-center my-2;
    }

    .version-edit__icon-container {
        @apply flex items-center;
    }

    .version-edit__icon-control {
        color: $text-filter-color;
        &:hover {
            @apply cursor-pointer;
            color: $text-filter-hover-color;
        }
    }

    .version-edit__icon-control_right {
        @apply ml-2;
    }

    .app__card-version-edit {
        @apply border rounded-lg border-solid p-3 mb-2;
        border-color: $border-version-block-color;
    }
    .app__card-version-title {
        @apply mb-3 text-center;
        font-size: $font-medium;
        color: $version-bloc-title-color;
    }

    .app__card-new-version-button {
        @apply rounded-full outline-none mb-2 right-0 relative;
        min-width: 10rem;
        background-color: $text-filter-color;
        &:hover {
            @apply cursor-pointer;
            background-color: $text-filter-hover-color;
        }
    }
    .app__card-version__delete-icon {
        @apply float-right;
    }
}
</style>
